import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import * as authApi from '../services/authService';
import { storeTokens, clearTokens, setSandboxSurface } from '../services/api';
import { onAuthStateChange, getSession, isConfigured as isSupabaseConfigured } from '../config/supabase';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  const [ineligibleFields, setIneligibleFields] = useState([]);
  const [drCareMemberId, setDrCareMemberId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isSystem, setIsSystem] = useState(false);
  // SERVER-OWNED admin capability. The server resolves BOTH role signals
  // (users.role AND users.role_ref_id -> roles/role_permissions) and hands the
  // answer down on /auth/me. The client NEVER re-derives it from a role string:
  // a custom RBAC role that legitimately grants admin must work, and a role
  // string alone must never be enough. Defaults to false — fail closed while
  // loading, on a failed /auth/me, and after logout.
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFullAdmin, setIsFullAdmin] = useState(false);
  // Sandbox (Test Admin) mode. Server-owned: it mirrors users.sandbox_mode, which only the
  // /admin/test-admin/enter|exit endpoints can flip. Read from the TOP LEVEL of /auth/me —
  // never from localStorage — so a hard reload always re-derives it from the database.
  //
  // 🔴 SINCE BOARD 08-05 TASK 6 THIS FLAG GATES NOTHING ON THE SERVER. Sandbox is scoped to
  // the SURFACE (`/api/sandbox/*` vs `/api/*`), not to the user. This value now means only
  // "which dashboard should this user land on", plus which API surface the client talks to.
  // The server re-derives sandbox from the mount path regardless of what the client believes.
  const [sandboxMode, setSandboxModeState] = useState(false);

  // Keep the API surface in lockstep with the flag. Wrapping the setter (rather than editing
  // each call site) means every existing path — /auth/me hydration, enter, exit, logout —
  // moves both together, so the client can never be pointed at the wrong surface.
  const setSandboxMode = useCallback((on) => {
    const next = on === true;
    setSandboxModeState(next);
    setSandboxSurface(next);
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (isSupabaseConfigured) {
          // Check for existing Supabase session
          const session = await getSession();

          if (session) {
            // Store tokens from Supabase session
            storeTokens(session.access_token, session.refresh_token, session.expires_at);

            // Fetch user data from our API
            const response = await authApi.getMe();
            setUser(response.user);
            setIsAuthenticated(true);
            setIsProfileComplete(response.isProfileComplete ?? true);
            setMissingFields(response.missingFields || []);
            setIneligibleFields(response.ineligibleFields || []);
            setDrCareMemberId(response.drCareMemberId || null);
            setPermissions(response.permissions || []);
            setIsSystem(response.isSystem === true);
            setIsAdmin(response.isAdmin === true);
            setIsFullAdmin(response.isFullAdmin === true);
            setSandboxMode(response.sandboxMode === true);
          } else {
            // Check localStorage token as fallback
            await tryLocalStorageToken();
          }
        } else {
          // Not using Supabase - check localStorage token directly
          await tryLocalStorageToken();
        }
      } catch {
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    const tryLocalStorageToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authApi.getMe();
          setUser(response.user);
          setIsAuthenticated(true);
          setIsProfileComplete(response.isProfileComplete ?? true);
          setMissingFields(response.missingFields || []);
          setIneligibleFields(response.ineligibleFields || []);
          setDrCareMemberId(response.drCareMemberId || null);
            setPermissions(response.permissions || []);
            setIsSystem(response.isSystem === true);
            setIsAdmin(response.isAdmin === true);
            setIsFullAdmin(response.isFullAdmin === true);
            setSandboxMode(response.sandboxMode === true);
        } catch {
          // Token invalid, clear it
          clearTokens();
        }
      }
    };

    initAuth();
  }, []);

  // Listen for Supabase auth state changes (only if configured)
  useEffect(() => {
    if (!isSupabaseConfigured) {
      // Not using Supabase, no need to listen for auth state changes
      return;
    }

    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        storeTokens(session.access_token, session.refresh_token, session.expires_at);

        // Fetch user data
        try {
          const response = await authApi.getMe();
          setUser(response.user);
          setIsAuthenticated(true);
          setIsProfileComplete(response.isProfileComplete ?? true);
          setMissingFields(response.missingFields || []);
          setIneligibleFields(response.ineligibleFields || []);
          setDrCareMemberId(response.drCareMemberId || null);
            setPermissions(response.permissions || []);
            setIsSystem(response.isSystem === true);
            setIsAdmin(response.isAdmin === true);
            setIsFullAdmin(response.isFullAdmin === true);
            setSandboxMode(response.sandboxMode === true);
        } catch {
          // User not found in our system yet
        }
      } else if (event === 'SIGNED_OUT') {
        clearTokens();
        setUser(null);
        setIsAuthenticated(false);
        setIsProfileComplete(true);
        setMissingFields([]);
        setDrCareMemberId(null);
        setPermissions([]);
        setIsSystem(false);
        setIsAdmin(false);
        setIsFullAdmin(false);
        setSandboxMode(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        storeTokens(session.access_token, session.refresh_token, session.expires_at);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    const response = await authApi.login({ email, password });

    // Store tokens from response
    storeTokens(response.token, response.refreshToken, response.expiresAt);

    setUser(response.user);
    setIsAuthenticated(true);

    // Fetch profile completeness status
    try {
      const meResponse = await authApi.getMe();
      setIsProfileComplete(meResponse.isProfileComplete ?? true);
      setMissingFields(meResponse.missingFields || []);
      setIneligibleFields(meResponse.ineligibleFields || []);
      setDrCareMemberId(meResponse.drCareMemberId || null);
      setPermissions(meResponse.permissions || []);
      setIsSystem(meResponse.isSystem === true);
      setIsAdmin(meResponse.isAdmin === true);
      setIsFullAdmin(meResponse.isFullAdmin === true);
      setSandboxMode(meResponse.sandboxMode === true);
    } catch {
      // Profile status check failed, assume complete to not block user.
      // Admin capability stays FALSE — never assume admin on a failed lookup.
      setIsProfileComplete(true);
    }

    return response;
  }, []);

  // Register
  const register = useCallback(async (data) => {
    const response = await authApi.register(data);

    // Note: With Supabase, registration requires email verification
    // Token may not be returned until email is verified
    if (response.token) {
      storeTokens(response.token, response.refreshToken, response.expiresAt);
      setUser(response.user);
      setIsAuthenticated(true);
    } else {
      // User registered but needs to verify email
      setUser(response.user);
    }

    return response;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    setIsProfileComplete(true);
    setMissingFields([]);
    setDrCareMemberId(null);
        setPermissions([]);
        setIsSystem(false);
        setIsAdmin(false);
        setIsFullAdmin(false);
        setSandboxMode(false);
  }, []);

  // Sign in with OAuth provider
  const signInWithOAuth = useCallback(async (provider) => {
    return authApi.signInWithOAuth(provider);
  }, []);

  // Update user data
  const updateUser = useCallback((updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  // Mark profile as complete. Exposed for the profile-completion flow; the live
  // path (CompleteProfilePage) refreshes via refreshUser, so this currently has
  // no in-app caller.
  const markProfileComplete = useCallback((userData, memberId) => {
    setIsProfileComplete(true);
    setMissingFields([]);
    if (memberId) {
      setDrCareMemberId(memberId);
    }
    if (userData) {
      setUser(userData);
    }
  }, []);

  // Check if user has role
  const hasRole = useCallback((roles) => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  }, [user]);

  // Refresh user data from API
  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getMe();
      setUser(response.user);
      setIsProfileComplete(response.isProfileComplete ?? true);
      setMissingFields(response.missingFields || []);
      setIneligibleFields(response.ineligibleFields || []);
      setDrCareMemberId(response.drCareMemberId || null);
            setPermissions(response.permissions || []);
            setIsSystem(response.isSystem === true);
            setIsAdmin(response.isAdmin === true);
            setIsFullAdmin(response.isFullAdmin === true);
            setSandboxMode(response.sandboxMode === true);
      return response.user;
    } catch {
      return null;
    }
  }, []);

  // Stale-claims policy: when an admin API call returns 403 while we still hold a live
  // session, the server has revoked a permission the UI still believes it has (e.g. Task 5
  // clears the server-side permission cache the instant a role's grants change). Refetch
  // /auth/me so the now-revoked permission drops out of the nav + route guards promptly,
  // instead of lingering until the next natural reload. Debounced to at most one refetch
  // per 3s so a burst of 403s can't storm the API; the original fetch is restored on cleanup.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const originalFetch = window.fetch;
    let lastRefetch = 0;
    const patchedFetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        if (response?.status === 403) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url ?? '');
          // React only to our own admin API — never to /auth/me itself (avoids a refetch loop)
          if (url.includes('/api/admin')) {
            const now = Date.now();
            if (now - lastRefetch > 3000) {
              lastRefetch = now;
              refreshUser();
            }
          }
        }
      } catch {
        // Never let interception affect the caller's response
      }
      return response;
    };
    window.fetch = patchedFetch;
    return () => {
      if (window.fetch === patchedFetch) window.fetch = originalFetch;
    };
  }, [isAuthenticated, refreshUser]);

  // Memoized context value
  
  // Check if user has a specific permission
  const hasPermission = useCallback((code) => {
    if (!user) return false;
    // System roles (e.g. Super Admin) hold EVERY permission — this mirrors the backend's
    // requirePermission bypass on roles.is_system. getMe sends isSystem + the full active
    // permission-code list, so no static role-name bypass is needed. This replaces the old
    // "legacy admin with empty permissions" shortcut, which conflated "still loading" with
    // "genuinely no permissions" and leaked full nav to zero-permission users.
    if (isSystem) return true;
    return permissions.includes(code);
  }, [user, isSystem, permissions]);


  const value = useMemo(() => ({
    user,
    isLoading,
    isAuthenticated,
    // SERVER-DERIVED, not a role-string list. The server resolves users.role
    // AND users.role_ref_id -> roles/role_permissions (auth.js
    // resolveAccessContext) and returns isAdmin/isFullAdmin on /auth/me, so a
    // custom RBAC role that legitimately grants admin reaches /admin and a
    // role string on its own never does. False until /auth/me answers.
    isAdmin,
    isFullAdmin,
    // Descriptive role-string helpers (UI copy / branching only — NOT gates).
    isStaff: user?.role === "staff",
    isAgencyAdmin: user?.role === "agency_admin",
    isDemo: user?.isDemo === true,
    isProfileComplete,
    needsProfileCompletion: isAuthenticated && !isProfileComplete,
    missingFields,
    ineligibleFields,
    drCareMemberId,
    login,
    register,
    logout,
    signInWithOAuth,
    updateUser,
    hasRole,
    refreshUser,
    permissions,
    isSystem,
    sandboxMode,
    hasPermission,
    markProfileComplete
  }), [user, isLoading, isAuthenticated, isProfileComplete, missingFields, ineligibleFields, drCareMemberId, login, register, logout, signInWithOAuth, updateUser, hasRole, refreshUser, markProfileComplete, permissions, isSystem, isAdmin, isFullAdmin, sandboxMode, hasPermission]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
